<script setup lang="ts">
  import { computed, ref, onMounted, onUnmounted, watch } from "vue";
  import { Text, IconSingleProduct, Avatar, Badge, IconWarningRound, IconASmall, IconBSmall } from "@ui-kit";
  import { formatDate } from "@src/utils";
  import { getPlace, getStatus } from "@src/dictionary";
  import { OrderTypeIcon } from "../OrderTypeIcon";

  const {
    order_number,
    order_total_items,
    order_status,
    order_place_from,
    order_place_to,
    order_place_section,
    createdAt,
    executerDisplayName,
    partial,
    comment,
    order_type,
    store,
    is_short = false,
  } = defineProps<{
    order_number: string;
    order_total_items: number;
    order_status: string;
    order_place_from: string;
    order_place_to: string;
    order_place_section: string;
    store?: string;
    order_type?: string;
    createdAt: string;
    executerDisplayName?: string;
    partial?: boolean;
    comment?: string;
    is_short?: boolean;
  }>();

  const emit = defineEmits<{ (e: "click"): void }>();

  const currentTime = ref(Date.now());
  let intervalId: ReturnType<typeof setInterval> | null = null;

  const startInterval = () => {
    if (order_status !== "request") return;

    if (intervalId) clearInterval(intervalId);

    intervalId = setInterval(() => {
      currentTime.value = Date.now();
    }, 60000);
  };

  const stopInterval = () => {
    if (!intervalId) return;

    clearInterval(intervalId);
    intervalId = null;
  };

  const executerInitials = computed(() => {
    const [firstName = "", lastName = ""] = executerDisplayName?.split(" ") || [];

    return { firstName, lastName };
  });

  const warningData = computed(() => {
    if (order_status !== "request") return null;

    const createdDate = new Date(createdAt);
    const now = new Date(currentTime.value);
    const diffInMinutes = (now.getTime() - createdDate.getTime()) / (1000 * 60);

    if (diffInMinutes > 10) {
      return {
        type: "danger",
        time: 10,
      };
    }

    if (diffInMinutes > 5) {
      return {
        type: "warning",
        time: 5,
      };
    }

    return null;
  });

  watch(
    () => order_status,
    (newStatus) => {
      if (newStatus === "request") startInterval();
      else stopInterval();
    },
  );

  onMounted(startInterval);
  onUnmounted(stopInterval);
</script>

<template>
  <div
    class="order-item"
    :class="[warningData ? warningData.type : '']"
    @click="emit('click')"
    data-testid="order-item"
  >
    <div class="status">
      <Badge
        isBold
        size="xs"
        :label="getStatus(order_status, partial).label"
        :variant="getStatus(order_status, partial).variant"
      />
      <Avatar
        v-if="executerInitials.firstName && executerInitials.lastName"
        :firstName="executerInitials.firstName"
        :lastName="executerInitials.lastName"
        size="s"
      />
      <Text font="body-regular-12" color="secondary" class="date-time">{{ formatDate(createdAt) }}</Text>
    </div>
    <div class="container">
      <div class="info">
        <div class="number-total">
          <Text font="body-medium-16">№ {{ order_number }}</Text>
          <div class="total-items">
            <IconSingleProduct style="width: 16px; height: 16px" />
            <Text font="body-medium-14">{{ order_total_items }}</Text>
          </div>
        </div>
        <div class="route">
          <div class="points">
            <IconASmall />
            <IconBSmall :class="`point-color-${order_place_section.toLowerCase()}`" />
          </div>
          <div class="route-text">
            <Text font="body-regular-14">
              {{ `${getPlace(order_place_from)}${store ? ": " + store : ""}` }}
            </Text>
            <Text font="body-regular-14">
              {{ `${order_place_section[0].toUpperCase()}・${getPlace(order_place_to)}` }}
            </Text>
          </div>
        </div>
      </div>
      <OrderTypeIcon :type="order_type" :size="32" />
    </div>
    <div v-if="comment" class="comment">
      <Text font="body-regular-14" :wrap="!is_short">{{ comment }}</Text>
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M6.57754 10L5.69519 8.58484C5.96257 8.41155 6.22995 8.19013 6.49733 7.92058C6.76471 7.65102 7.01426 7.35259 7.24599 7.02527C7.47772 6.69795 7.62923 6.39952 7.70054 6.12996C7.20143 6.0722 6.77362 5.87004 6.41711 5.52347C6.07843 5.1769 5.90909 4.69555 5.90909 4.07942C5.90909 3.40554 6.11408 2.89531 6.52406 2.54874C6.93405 2.18291 7.41533 2 7.96791 2C8.68093 2.01925 9.19786 2.30806 9.51872 2.86643C9.83957 3.42479 10 4.0698 10 4.80144C10 5.59085 9.83957 6.34176 9.51872 7.05415C9.19786 7.76655 8.77005 8.38267 8.23529 8.90253C7.71836 9.40313 7.16578 9.76895 6.57754 10ZM0.882353 10L0 8.58484C0.249554 8.41155 0.508021 8.19013 0.775401 7.92058C1.06061 7.65102 1.31016 7.35259 1.52406 7.02527C1.75579 6.69795 1.91622 6.39952 2.00535 6.12996C1.50624 6.0722 1.07843 5.87004 0.721925 5.52347C0.383244 5.1769 0.213904 4.69555 0.213904 4.07942C0.213904 3.40554 0.409982 2.89531 0.802139 2.54874C1.21212 2.18291 1.70232 2 2.27273 2C2.96791 2.01925 3.47594 2.30806 3.79679 2.86643C4.11765 3.42479 4.27807 4.0698 4.27807 4.80144C4.27807 5.59085 4.10873 6.34176 3.77005 7.05415C3.4492 7.76655 3.0303 8.38267 2.51337 8.90253C2.01426 9.40313 1.47059 9.76895 0.882353 10Z"
          fill="var(--sf-base-text-primary)"
        />
      </svg>
    </div>
    <div v-if="!is_short && warningData" class="attention" :class="[warningData.type]">
      <IconWarningRound style="width: 16px; height: 16px" />
      <Text font="body-medium-12" color="white">
        {{ $t(`Заказ в ожидании более {time}-ти минут`, { time: warningData?.time }) }}
      </Text>
    </div>
  </div>
</template>

<style scoped>
  .order-item {
    display: flex;
    flex-direction: column;
    max-width: 328px;
    gap: 8px;
    padding: 12px 16px 16px;
    background-color: var(--sf-base-background-white);
    border-radius: 16px;
    border: 2px solid transparent;

    &.warning {
      border-color: var(--sf-base-icon-warning);
    }

    &.danger {
      border-color: var(--sf-base-icon-error);
    }

    .status {
      display: flex;
      gap: 4px;

      .date-time {
        margin-left: auto;
      }

      & :deep(.badge) {
        padding: 4px 8px;
        font-weight: 600;
        white-space: nowrap;
      }
    }

    .container {
      display: flex;
      justify-content: space-between;

      .info {
        display: flex;
        flex-direction: column;
        gap: 6px;

        .number-total {
          display: flex;
          gap: 8px;
        }

        .total-items {
          display: flex;
          align-items: center;
          gap: 3px;
          padding: 1px 6px 1px 4px;
          border-radius: 100px;
          background-color: var(--sf-base-background-secondary);
        }

        .route {
          display: flex;
          gap: 8px;
        }

        .points {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          align-items: center;
          padding: 2px 0;
          position: relative;
          width: 16px;
          min-height: 36px;
          z-index: 0;

          .point-color-woman :deep(rect) {
            fill: var(--sf-base-icon-error);
          }
          .point-color-man :deep(rect) {
            fill: var(--sf-base-icon-info);
          }
          .point-color-kids :deep(rect) {
            fill: var(--sf-base-icon-success);
          }

          & > * {
            position: relative;
            z-index: 1;
          }

          &::before {
            content: "";
            position: absolute;
            top: 9px;
            bottom: 9px;
            left: 50%;
            border-left: 2px dashed var(--sf-base-background-dark);
            transform: translateX(-50%);
            pointer-events: none;
            z-index: 0;
          }
        }

        .route-text {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
      }
    }

    .comment {
      display: flex;
      gap: 6px;
      padding: 6px 10px;
      background-color: var(--sf-base-background-secondary);
      border-radius: 8px;
      overflow: hidden;

      & :deep(.text) {
        overflow: hidden;
        text-overflow: ellipsis;
      }

      svg {
        flex-shrink: 0;
      }
    }

    .attention {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px 8px;
      border-radius: 8px;

      & :deep(.icon path) {
        stroke: var(--sf-tag-text-error);
        stroke-width: 3px;
      }

      &.warning {
        background-color: var(--sf-tag-background-warning);
      }

      &.danger {
        background-color: var(--sf-tag-background-error);
      }
    }
  }
</style>
